import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Button from "../Button/Button";
import "./Modal.scss";

function Modal({ 
    isOpen, 
    onClose, 
    title = "Modal Title", 
    children, 
    buttons = [] 
}) {
    const modalRef = useRef(null);
    const [closing, setClosing] = useState(false);

    const handleClose = useCallback(() => {
        setClosing(true);
        playSound("/sounds/close.mp3");

        setTimeout(() => {
            setClosing(false);
            if (onClose) onClose();
        }, 300);
    }, [onClose]);

    const playSound = (soundPath) => {
        const audio = new Audio(soundPath);
        audio.volume = 0.3;
        audio.play().catch(() => console.warn("Audio playback failed. User interaction required."));
    };

    useEffect(() => {
        if (isOpen) {
            playSound("/sounds/open.mp3");
        }
    }, [isOpen]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                handleClose();
            }
        };

        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                handleClose();
            }
        };

        if (isOpen) {
            window.addEventListener("keydown", handleKeyDown);
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, handleClose]);

    if (!isOpen) return null;

    return createPortal(
        <div className="modal-overlay">
            <motion.div
                ref={modalRef}
                className={`modal-container ${closing ? "modal-hidden" : ""}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <div className="modal-header">
                    <h2>{title}</h2>
                    <span className="close" onClick={handleClose}>&times;</span>
                </div>
                <div className="modal-bar"></div>
                <div className="modal-content">
                    {children}
                </div>
                {buttons.length > 0 && (
                    <div className="modal-footer">
                        {buttons.map((button, index) => (
                            <Button 
                                key={index} 
                                label={button.label} 
                                onClick={button.onClick} 
                                type={button.type} 
                                className={button.className}
                            />
                        ))}
                    </div>
                )}
            </motion.div>
        </div>,
        document.body
    );
}

export default Modal;
